import isEqual from 'lodash.isequal';
import { useContext, useEffect, useMemo, useState } from 'react';
import { setBannerDismissedCookie } from '@/srp/PromoBanner/utils';
import { COOKIES } from '~/constants/srp';
import TagId from '~/constants/srp/TagId';
import { useIsNative } from '~/context/common/DeviceContext';
import { useNativeSupportsDismissBanner } from '~/context/common/NativeContext';
import AppliedFiltersContext from '~/context/srp/AppliedFiltersContext';
import { useSavedSearches } from '~/context/srp/SavedSearchesContext';
import StaticContext from '~/context/srp/StaticContext';
import decodeCvnaId from '~/utilities/srp/cvnaId';
import { Dismissed, NewPriceFilter, PromoBanners } from '../constants';

export type BannerDismissed = {
  isEarthDismissed: Dismissed;
  isEVDismissed: Dismissed;
  isPriceDropDismissed: Dismissed;
};

export default function useHandleShowLogic(banner: PromoBanners) {
  const nativeSupportsDismissedBanners = useNativeSupportsDismissBanner();
  const { dismissedEVBanner, dismissedEarthDayBanner, dismissedNewPrice } =
    useContext(StaticContext);
  const savedSearches = useSavedSearches();
  const {
    appliedFilters: { tagIds }
  } = useContext(AppliedFiltersContext);
  const isNative = useIsNative();
  const [localDismissed, setLocalDismissed] = useState<BannerDismissed>({
    isEarthDismissed: dismissedEarthDayBanner,
    isEVDismissed: dismissedEVBanner,
    isPriceDropDismissed: dismissedNewPrice
  });
  const hasEVFilter = tagIds?.includes(TagId.EVCredit.toString()) ?? false;

  useEffect(() => {
    if (banner !== PromoBanners.PriceDrop) return;
    // Dismiss the banner if the user has a saved search with the new price filter
    const hasNewPriceSavedSearch = savedSearches.some(savedSearch =>
      isEqual(decodeCvnaId(savedSearch.url).filters, NewPriceFilter)
    );

    if (hasNewPriceSavedSearch) {
      setBannerDismissedCookie(
        banner,
        COOKIES.dismissedNewPriceBanner,
        !nativeSupportsDismissedBanners
      );
      setLocalDismissed(prevState => ({
        ...prevState,
        isPriceDropDismissed: Dismissed.Banner
      }));
    }
  }, [banner, nativeSupportsDismissedBanners, savedSearches]);

  useEffect(() => {
    if (banner !== PromoBanners.PriceDrop) return;
    // Hide Banner if user has applied the new price filter
    const hasNewPriceFilter =
      tagIds?.includes(TagId.PriceDrop.toString()) ?? false;
    if (hasNewPriceFilter) {
      setLocalDismissed(prevState => ({
        ...prevState,
        isPriceDropDismissed: Dismissed.Banner
      }));
    }
  }, [banner, tagIds]);

  const shouldShow = useMemo(() => {
    switch (banner) {
      case PromoBanners.Earth:
        return (
          localDismissed.isEarthDismissed < Dismissed.Banner &&
          !isNative &&
          !hasEVFilter
        );
      case PromoBanners.Ev:
        return (
          localDismissed.isEVDismissed < Dismissed.Banner &&
          !isNative &&
          !hasEVFilter
        );
      case PromoBanners.PriceDrop:
        return localDismissed.isPriceDropDismissed < Dismissed.Banner;
    }
  }, [
    banner,
    hasEVFilter,
    isNative,
    localDismissed.isEVDismissed,
    localDismissed.isEarthDismissed,
    localDismissed.isPriceDropDismissed
  ]);

  return {
    shouldShow,
    setLocalDismissed
  };
}
